import localFont from 'next/font/local';
import { Typography } from '@eventbrite/marmalade';
import React, { RefObject, useCallback, useRef } from 'react';
import styles from './GuideCard.module.scss';
import { Guide } from '@/types/guides';
import Image from 'next/image';
import { GradientTypography } from '../GradientTypography';
import { getGuideUrl } from '../utils/urls';
import { trackGuideEvent } from '@/components/guides/utils/tracking';
import { useIntersection } from 'react-use';

const foundersGroteskRegular = localFont({
    src: '../fonts/founders-grotesk-r-bold.woff2',
    display: 'swap',
});

interface GuideCardProps {
    guide: Guide;
}

export const GuideCard = ({ guide }: GuideCardProps) => {
    const { owner, name, coverImage, titleBackgroundGradient } = guide;
    const { relativePath } = getGuideUrl(guide);
    const guideUrl = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath;
    const cardRef = useRef<HTMLAnchorElement>(null);
    const impressionTracked = useRef(false);

    // react-use@17's useIntersection types the ref as RefObject<HTMLElement>
    // (non-null), but @types/react@19 makes useRef<T>(null) return
    // RefObject<T | null>. The hook handles a null current at runtime, so bridge
    // the ref type to the hook's expected non-null signature.
    const intersection = useIntersection(cardRef as RefObject<HTMLElement>, {
        root: null,
        rootMargin: '0px',
        threshold: 0.25,
    });

    React.useEffect(() => {
        if (intersection?.isIntersecting && !impressionTracked.current) {
            trackGuideEvent({
                eventName: 'ViewSuggestedGuides',
                guide,
            });
            impressionTracked.current = true;
        }
    }, [intersection?.isIntersecting, guide]);

    const handleClick = useCallback(() => {
        trackGuideEvent({
            eventName: 'ViewGuide',
            guide,
        });
    }, [trackGuideEvent, guide]);

    return (
        <a
            href={guideUrl}
            onClick={handleClick}
            tabIndex={0}
            data-testid="guide-card"
            ref={cardRef}
            className={styles.root}
        >
            <Image
                src={coverImage?.mediaUrl}
                alt={name}
                className={styles.backgroundImage}
                fill
                sizes="(max-width: 768px) 100vw, 240px"
            />
            <div className={styles.cardContent}>
                <div
                    className={styles.authorAvatar}
                    style={
                        {
                            '--backgroundColor': titleBackgroundGradient?.endColor,
                        } as React.CSSProperties
                    }
                >
                    <Image
                        src={owner.imageUrl}
                        alt={owner.displayName}
                        className={styles.authorImage}
                        width={26}
                        height={26}
                    />
                </div>
                <div
                    className={styles.authorName}
                    style={
                        {
                            '--backgroundColor': titleBackgroundGradient?.endColor,
                        } as React.CSSProperties
                    }
                >
                    <Typography
                        variant="display-sm"
                        color="neutral-1000"
                        style={
                            {
                                fontFamily: foundersGroteskRegular.style.fontFamily,
                                fontSize: '10px',
                                fontWeight: 'unset',
                                lineHeight: '1.1em',
                                letterSpacing: '0.01em',
                                '--TypographyColor': '#13002d',
                            } as React.CSSProperties
                        }
                    >
                        {owner.displayName}
                    </Typography>
                </div>
                <GradientTypography
                    className={styles.title}
                    fontSize="18px"
                    startColor={titleBackgroundGradient?.endColor}
                    endColor={titleBackgroundGradient?.startColor}
                >
                    {name}
                </GradientTypography>
            </div>
            {/* EB-307354 Removing this until we reliably get a (filtered) "live" event count */}
            {/* {guide.events?.length > 0 && (
                <div className={styles.numEvents}>
                    <Typography variant="display-sm" color="neutral-1000">
                        {guide.events?.length} Events
                    </Typography>
                </div>
            )} */}
        </a>
    );
};
